import rawConfig from './config.json';
import rawPatients from './patients.json';
import rawRooms from './rooms.json';
import rawRoomVisuals from './room-visuals.json';

export interface ContentValidationError {
  file: string;
  field: string;
  message: string;
}

/**
 * Validasi ketat seluruh konfigurasi konten JSON saat aplikasi boot.
 * Mengembalikan array ContentValidationError jika ada yang tidak valid.
 */
export function validateGameContent(
  config: unknown = rawConfig,
  rooms: unknown = rawRooms,
  roomVisuals: unknown = rawRoomVisuals,
  patients: unknown = rawPatients
): ContentValidationError[] {
  const errors: ContentValidationError[] = [];

  // 1. Validasi config.json
  if (!config || typeof config !== 'object') {
    errors.push({ file: 'config.json', field: 'root', message: 'Config harus berupa objek' });
  } else {
    const c = config as Record<string, unknown>;
    if (!Number.isInteger(c.startingMoney) || (c.startingMoney as number) < 0) {
      errors.push({ file: 'config.json', field: 'startingMoney', message: 'startingMoney harus integer >= 0' });
    }
    if (!Number.isInteger(c.defaultSeed)) {
      errors.push({ file: 'config.json', field: 'defaultSeed', message: 'defaultSeed harus integer' });
    }

    const rep = c.reputation as Record<string, unknown> | undefined;
    if (!rep || typeof rep !== 'object') {
      errors.push({ file: 'config.json', field: 'reputation', message: 'Field reputation wajib ada' });
    } else {
      if (!Number.isInteger(rep.initial) || (rep.initial as number) < 0) {
        errors.push({ file: 'config.json', field: 'reputation.initial', message: 'reputation.initial harus integer >= 0' });
      }
      if (!Number.isInteger(rep.max) || (rep.max as number) <= (rep.min as number)) {
        errors.push({ file: 'config.json', field: 'reputation.max', message: 'reputation.max harus lebih besar dari min' });
      }
    }

    const grid = c.grid as Record<string, unknown> | undefined;
    if (!grid || typeof grid !== 'object') {
      errors.push({ file: 'config.json', field: 'grid', message: 'Field grid wajib ada' });
    } else {
      if (!Number.isInteger(grid.cols) || (grid.cols as number) <= 0) {
        errors.push({ file: 'config.json', field: 'grid.cols', message: 'grid.cols harus integer > 0' });
      }
      if (!Number.isInteger(grid.floors) || (grid.floors as number) <= 0) {
        errors.push({ file: 'config.json', field: 'grid.floors', message: 'grid.floors harus integer > 0' });
      }
    }
  }

  // 2. Validasi rooms.json
  const roomIds = new Set<string>();
  if (!Array.isArray(rooms) || rooms.length === 0) {
    errors.push({ file: 'rooms.json', field: 'root', message: 'rooms harus berupa array non-kosong' });
  } else {
    rooms.forEach((r, idx) => {
      if (!r || typeof r !== 'object') {
        errors.push({ file: 'rooms.json', field: `[${idx}]`, message: 'Room harus berupa objek' });
        return;
      }
      const item = r as Record<string, unknown>;
      if (typeof item.id !== 'string' || item.id.trim() === '') {
        errors.push({ file: 'rooms.json', field: `[${idx}].id`, message: 'id wajib string non-kosong' });
      } else {
        if (roomIds.has(item.id)) {
          errors.push({ file: 'rooms.json', field: `[${idx}].id`, message: `id ganda: ${item.id}` });
        }
        roomIds.add(item.id);
      }
      if (!Number.isInteger(item.width) || (item.width as number) <= 0) {
        errors.push({ file: 'rooms.json', field: `[${idx}].width`, message: 'width harus integer > 0' });
      }
      if (!Number.isInteger(item.cost) || (item.cost as number) < 0) {
        errors.push({ file: 'rooms.json', field: `[${idx}].cost`, message: 'cost harus integer >= 0' });
      }
    });
  }

  // 3. Validasi room-visuals.json
  if (!Array.isArray(roomVisuals)) {
    errors.push({ file: 'room-visuals.json', field: 'root', message: 'room-visuals harus berupa array' });
  } else {
    const visualIds = new Set<string>();
    roomVisuals.forEach((v, idx) => {
      const item = v as Record<string, unknown>;
      if (typeof item.id !== 'string' || !roomIds.has(item.id)) {
        errors.push({ file: 'room-visuals.json', field: `[${idx}].id`, message: `Visual id tidak ada di rooms.json: ${item.id}` });
      } else {
        visualIds.add(item.id);
      }
      if (typeof item.name !== 'string' || item.name.trim() === '') {
        errors.push({ file: 'room-visuals.json', field: `[${idx}].name`, message: 'name wajib string non-kosong' });
      }
      if (typeof item.color !== 'string' || !item.color.startsWith('#')) {
        errors.push({ file: 'room-visuals.json', field: `[${idx}].color`, message: 'color wajib hex string (#)' });
      }
    });

    // Pastikan setiap room punya visual
    roomIds.forEach((id) => {
      if (!visualIds.has(id)) {
        errors.push({ file: 'room-visuals.json', field: id, message: `Ruangan ${id} belum memiliki definisi visual` });
      }
    });
  }

  // 4. Validasi patients.json
  if (!Array.isArray(patients) || patients.length === 0) {
    errors.push({ file: 'patients.json', field: 'root', message: 'patients harus berupa array non-kosong' });
  } else {
    const patientIds = new Set<string>();
    patients.forEach((p, idx) => {
      const item = p as Record<string, unknown>;
      if (typeof item.typeId !== 'string' || item.typeId.trim() === '') {
        errors.push({ file: 'patients.json', field: `[${idx}].typeId`, message: 'typeId wajib string' });
      } else {
        if (patientIds.has(item.typeId)) {
          errors.push({ file: 'patients.json', field: `[${idx}].typeId`, message: `typeId ganda: ${item.typeId}` });
        }
        patientIds.add(item.typeId);
      }
      if (!Number.isInteger(item.patienceMs) || (item.patienceMs as number) <= 0) {
        errors.push({ file: 'patients.json', field: `[${idx}].patienceMs`, message: 'patienceMs harus integer > 0' });
      }
      if (!Number.isInteger(item.serviceDurationMs) || (item.serviceDurationMs as number) <= 0) {
        errors.push({ file: 'patients.json', field: `[${idx}].serviceDurationMs`, message: 'serviceDurationMs harus integer > 0' });
      }
      if (!Number.isInteger(item.fee) || (item.fee as number) < 0) {
        errors.push({ file: 'patients.json', field: `[${idx}].fee`, message: 'fee harus integer >= 0' });
      }
      if (!Number.isInteger(item.spawnWeight) || (item.spawnWeight as number) <= 0) {
        errors.push({ file: 'patients.json', field: `[${idx}].spawnWeight`, message: 'spawnWeight harus integer > 0' });
      }
    });
  }

  return errors;
}

export function assertGameContentValid(): void {
  const errors = validateGameContent();
  if (errors.length > 0) {
    const details = errors.map((e) => `[${e.file}] ${e.field}: ${e.message}`).join('\n');
    throw new Error(`Data konten game korup atau tidak valid:\n${details}`);
  }
}

